/**
 * useVitalsTransition — v10.3.1 Fase 2
 *
 * Interpola suavemente los signos vitales cuando el instructor envía una
 * transición progresiva (`transition_duration_ms > 0`). El backend guarda
 * el estado TARGET inmediatamente en Mongo y broadcasta metadata
 * `transition: { from, duration_ms, started_at }` para que el cliente
 * anime de `from` → `target` en su reloj local.
 *
 * Comportamiento:
 *   - Campos numéricos (fc, spo2, pa_sistolica, pa_diastolica, fr,
 *     temperatura, ecg_amplitude, pleth_amplitude, respiratory_amplitude,
 *     glasgow, perfusion, noise_level) se interpolan linealmente.
 *   - Campos categóricos (ritmo_ecg, signal_quality, artifact_type,
 *     patron_respiratorio) se aplican INSTANTÁNEAMENTE al inicio de la
 *     transición (per spec del usuario: cambio de ritmo → cardioversión,
 *     no se "morfa" gradualmente).
 *   - Una transición nueva CANCELA la anterior.
 *
 * Uso:
 *   const displayedVitals = useVitalsTransition(rawVitals, activeTransition, setRawVitals);
 *   <VitalMonitor vitals={displayedVitals} ... />
 */
import { useEffect, useRef, useState } from 'react';

const NUMERIC_FIELDS = new Set([
  'fc', 'spo2', 'pa_sistolica', 'pa_diastolica', 'fr', 'temperatura',
  'ecg_amplitude', 'pleth_amplitude', 'respiratory_amplitude',
  'glasgow', 'perfusion', 'noise_level', 'pulse_rate',
]);

const CATEGORICAL_INSTANT = new Set([
  'ritmo_ecg', 'signal_quality', 'artifact_type', 'patron_respiratorio',
]);

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function interpolateVitals(fromV, targetV, progress) {
  const eased = easeInOutCubic(progress);
  const out = { ...targetV };
  for (const k of Object.keys(targetV)) {
    if (NUMERIC_FIELDS.has(k)) {
      const fromN = typeof fromV[k] === 'number' ? fromV[k] : targetV[k];
      const toN = typeof targetV[k] === 'number' ? targetV[k] : fromN;
      if (typeof fromN === 'number' && typeof toN === 'number') {
        out[k] = fromN + (toN - fromN) * eased;
        // Redondear vitales enteros (FC, FR, PA) para no mostrar decimales
        if (k !== 'temperatura' && k !== 'ecg_amplitude' && k !== 'pleth_amplitude'
            && k !== 'respiratory_amplitude' && k !== 'perfusion' && k !== 'noise_level') {
          out[k] = Math.round(out[k]);
        } else {
          // Un decimal para valores continuos
          out[k] = Math.round(out[k] * 10) / 10;
        }
      }
    }
    // Campos categóricos: ya están en `targetV`, se aplican al t=0
  }
  return out;
}

/**
 * Aplica interpolación local sobre `rawVitals`. Si no hay transición
 * activa, devuelve `rawVitals` sin cambios.
 *
 * `transition` shape: { from: {...}, duration_ms: number, started_at: ISO,
 *                        target: {...}, appliedAtMs: number (perf.now) }
 * El caller debe pasar el `target` capturado al recibir el evento WS.
 */
export function useVitalsTransition(rawVitals, transition) {
  const [displayed, setDisplayed] = useState(rawVitals);
  const rafRef = useRef(null);
  const activeRef = useRef(null);

  // Sin transición: sincronizar directo
  useEffect(() => {
    if (!transition) {
      setDisplayed(rawVitals);
      activeRef.current = null;
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    }
  }, [rawVitals, transition]);

  // Nueva transición: iniciar rAF loop
  useEffect(() => {
    if (!transition || !transition.target || !transition.from) return undefined;
    const startedAtMs = transition.appliedAtMs ?? performance.now();
    const duration = Math.max(200, transition.duration_ms || 0);
    activeRef.current = { ...transition, startedAtMs, duration };

    // Estado inicial: from (con categóricas ya del target)
    const initialFrame = interpolateVitals(transition.from, transition.target, 0);
    setDisplayed({ ...transition.from, ...initialFrame });

    const tick = () => {
      const now = performance.now();
      const t = Math.min(1, (now - startedAtMs) / duration);
      const frame = interpolateVitals(transition.from, transition.target, t);
      setDisplayed((prev) => ({ ...prev, ...frame }));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        // Terminar en el target exacto
        setDisplayed({ ...transition.target });
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    };
  }, [transition]);

  return displayed;
}

/**
 * Helper para el instructor: construir el payload de WS `update_vitals`
 * con la duración de transición configurada.
 */
export function buildVitalsUpdatePayload(changes, opts = {}) {
  const p = { vitals: changes };
  if (opts.reason) p.reason = opts.reason;
  if (opts.transitionDurationMs && opts.transitionDurationMs > 0) {
    p.transition_duration_ms = Math.floor(opts.transitionDurationMs);
  }
  return p;
}

export const NUMERIC_VITAL_FIELDS = NUMERIC_FIELDS;
export const CATEGORICAL_VITAL_FIELDS = CATEGORICAL_INSTANT;
